"use client";;!function(){try{var e="undefined"!=typeof window?window:"undefined"!=typeof global?global:"undefined"!=typeof globalThis?globalThis:"undefined"!=typeof self?self:{};e._sentryModuleMetadata=e._sentryModuleMetadata||{},e._sentryModuleMetadata[(new e.Error).stack]=Object.assign({},e._sentryModuleMetadata[(new e.Error).stack],{"_sentryBundlerPluginAppKey:ENCON_SENTRY_KEY":true});}catch(e){}}();
import { Turnstile } from "@marsidev/react-turnstile";
import type { SerializedEditorState } from "@payloadcms/richtext-lexical/lexical";
import { useRouter } from "next/navigation";
import type React from "react";
import { useCallback, useRef, useState } from "react";
import { FormProvider, useForm } from "react-hook-form";
import RichText from "@/components/RichText";
import { Button } from "@/components/ui/button";
import type { Form as FormType, Job } from "@/payload-types";
import "./index.scss";

import { useLocale, useTranslations } from "next-intl";
import Container from "@/components/Container";
import { getClientSideURL, getServerSideURL } from "@/utilities/getURL";
import { fields } from "./fields";
import { OpenQuestionField } from "./OpenQuestion";

export type FormBlockType = {
	blockName?: string;
	blockType?: "formBlock";
	enableIntro: boolean;
	form: FormType;
	introContent?: SerializedEditorState;
	slug?: string;
	openQuestions?: Job["openQuestions"];
};

interface FormField {
	field: string;
	value: unknown;
}

const buildOpenQuestionAnswers = (
	data: Record<string, unknown>,
	openQuestions?: Job["openQuestions"],
) =>
	openQuestions
		?.filter((q) => q.kind !== "infobox")
		.map((q) => {
			const value = data[`oq_${q.questionId}`];
			if (value === undefined || value === "" || value === null) return null;
			if (q.kind === "boolean" || q.kind === "legal") {
				return { open_question_id: q.questionId, flag: !!value };
			}
			if (q.kind === "multi_choice") {
				return { open_question_id: q.questionId, multi_content: value };
			}
			return { open_question_id: q.questionId, content: String(value) };
		})
		.filter(Boolean) ?? [];

export const FormBlock: React.FC<
	{
		id?: string;
	} & FormBlockType
> = (props) => {
	const {
		enableIntro,
		form: formFromProps,
		form: {
			id: formID,
			confirmationMessage,
			confirmationType,
			redirect,
			isRecruiteeForm,
			isFlexmailForm,
			flexmailSourceID,
			disableCaptcha,
			submitButtonLabel,
		} = {},
		introContent,
		slug,
		openQuestions,
	} = props;

	const formMethods = useForm({
		defaultValues: formFromProps.fields as any,
	});
	const {
		control,
		formState: { errors },
		handleSubmit,
		register,
		setValue,
	} = formMethods;

	const [isLoading, setIsLoading] = useState(false);
	const [isSubmitting, setIsSubmitting] = useState(false);
	const turnstileToken = useRef<string | null>(null);
	const locale = useLocale();
	const [hasSubmitted, setHasSubmitted] = useState<boolean>();
	const [error, setError] = useState<
		{ message: string; status?: string } | undefined
	>();
	const router = useRouter();
	const t = useTranslations("Footer");

	const uploadFile = useCallback(
		async (
			file: File,
			collectionSlug: string,
		): Promise<{ id: string; filename: string }> => {
			const formData = new FormData();
			formData.append("file", file);

			// PayloadCMS expects JSON data in _payload field
			formData.append(
				"_payload",
				JSON.stringify({
					alt: file.name,
				}),
			);

			try {
				const response = await fetch(
					`${getServerSideURL()}/api/${collectionSlug}`,
					{
						method: "POST",
						body: formData,
					},
				);

				if (!response.ok) {
					const errorData = await response.json().catch(() => ({}));
					const errorMessage =
						errorData.errors?.[0]?.message ||
						response.statusText ||
						"File upload failed";
					throw new Error(`File upload failed: ${errorMessage}`);
				}

				const result = await response.json();
				if (result.doc?.id) {
					return {
						id: result.doc.id,
						filename: result.doc.filename || file.name,
					};
				}
				throw new Error("File upload failed: No document ID returned");
			} catch (error) {
				console.error("File upload error:", error);
				throw error;
			}
		},
		[],
	);

	const formatFormData = useCallback(
		async (data: Record<string, unknown>): Promise<FormField[]> => {
			const submissionData: FormField[] = [];
			const fields = formFromProps?.fields
				?.filter(
					(field): field is Extract<typeof field, { name: string }> =>
						"name" in field,
				)
				.map((field) => ({
					name: field.name,
					blockType: field.blockType,
					uploadCollection:
						"uploadCollection" in field ? field.uploadCollection : undefined,
				}));

			for (const [name, value] of Object.entries(data)) {
				const field = fields?.find((f) => f.name === name);

				// Check if it's a file field by blockType or if value is FileList
				if (field?.blockType === "upload" && value instanceof FileList) {
					if (value.length > 0) {
						// Upload file first and get the file ID
						// This will throw an error if upload fails, preventing form submission
						const collectionSlug =
							field.uploadCollection || "submittedDocuments";
						const uploadResult = await uploadFile(value[0], collectionSlug);
						// PayloadCMS relationship fields expect just the ID string
						submissionData.push({
							field: name,
							value: uploadResult.id,
						});
					}
				} else if (value !== undefined) {
					submissionData.push({ field: name, value });
				}
			}

			return submissionData;
		},
		[formFromProps?.fields, uploadFile],
	);

	const onSubmit = useCallback(
		(data: Record<string, unknown>) => {
			let loadingTimerID: ReturnType<typeof setTimeout>;
			const submitForm = async () => {
				if (isSubmitting) return;
				setIsSubmitting(true);
				setError(undefined);

				const language = locale ? locale : "en";

				// delay loading indicator by 1s
				loadingTimerID = setTimeout(() => {
					setIsLoading(true);
				}, 1000);

				try {
					if (isRecruiteeForm) {
						const recruiteeFormData = new FormData();
						if (slug) recruiteeFormData.append("slug", slug);
						if (formID) recruiteeFormData.append("formID", formID);

						const formFields =
							formFromProps?.fields?.filter(
								(field): field is Extract<typeof field, { name: string }> =>
									"name" in field,
							) ?? [];

						for (const field of formFields) {
							const value = data[field.name];
							if (field.blockType === "upload" && value instanceof FileList) {
								if (value.length > 0) {
									recruiteeFormData.append(field.name, value[0]);
								}
							} else if (value !== undefined && value !== null) {
								recruiteeFormData.append(field.name, String(value));
							}
						}

						const openQuestionAnswers = buildOpenQuestionAnswers(
							data,
							openQuestions,
						);
						if (openQuestionAnswers.length > 0) {
							recruiteeFormData.append(
								"openQuestionAnswers",
								JSON.stringify(openQuestionAnswers),
							);
						}

						const recruiteeRes = await fetch("/api/recruitee", {
							method: "POST",
							headers: {
								"x-turnstile-token": turnstileToken.current || "",
							},
							body: recruiteeFormData,
						});

						clearTimeout(loadingTimerID);
						setIsLoading(false);
						setIsSubmitting(false);

						if (!recruiteeRes.ok) {
							const errBody = await recruiteeRes.json().catch(() => ({}));
							setError({
								message:
									errBody.error ||
									"There was an error processing your request. Please try again later.",
								status: String(recruiteeRes.status),
							});
							return;
						}

						setHasSubmitted(true);

						if (confirmationType === "redirect" && redirect) {
							const { url } = redirect;
							if (url) router.push(url);
						}

						return;
					}

					const submissionData = await formatFormData(data);

					const req = await fetch(
						`${getClientSideURL()}/api/form-submissions?locale=${locale}`,
						{
							body: JSON.stringify({
								form: formID,
								submissionData,
								pageUrl: window.location.href,
							}),
							headers: {
								"Content-Type": "application/json",
								"x-turnstile-token": turnstileToken.current || "",
							},
							method: "POST",
						},
					);

					const res = await req.json();

					clearTimeout(loadingTimerID);

					if (req.status >= 400) {
						setIsLoading(false);
						setIsSubmitting(false);

						setError({
							message: res.errors?.[0]?.message || "Internal Server Error",
							status: res.status,
						});

						return;
					}

					// Main form submission succeeded, now handle secondary services
					let secondaryServiceError = false;
					let flexmailDuplicate = false;

					if (isFlexmailForm) {
						try {
							const flexmailRes = await fetch("/api/flexmail", {
								method: "POST",
								headers: {
									"Content-Type": "application/json",
								},
								body: JSON.stringify({
									data,
									language,
									sourceId: flexmailSourceID,
								}),
							});
							clearTimeout(loadingTimerID);

							if (!flexmailRes.ok) {
								const errBody = await flexmailRes.json().catch(() => ({}));
								const errText = JSON.stringify(errBody).toLowerCase();
								flexmailDuplicate =
									flexmailRes.status === 409 ||
									/duplicate|already\s+(exist|subscribed|registered)|contact.*exist/.test(
										errText,
									);
								console.error(
									"Flexmail submission failed:",
									flexmailRes.status,
									errBody,
								);
								secondaryServiceError = true;
							}
						} catch (flexmailErr) {
							console.error("Flexmail error:", flexmailErr);
							secondaryServiceError = true;
						}
					}

					setIsLoading(false);
					setIsSubmitting(false);

					// Only show success if secondary services didn't fail
					if (secondaryServiceError) {
						setHasSubmitted(false);
						setError({
							message: flexmailDuplicate
								? t("alreadySubscribed")
								: "There was an error processing your request. Please try again later.",
						});
					} else {
						setHasSubmitted(true);
					}

					if (confirmationType === "redirect" && redirect) {
						const { url } = redirect;

						const redirectUrl = url;

						if (redirectUrl) router.push(redirectUrl);
					}
				} catch (err) {
					console.warn(err);
					clearTimeout(loadingTimerID);
					setHasSubmitted(false);
					setIsLoading(false);
					setIsSubmitting(false);
					setError({
						message: "Something went wrong.",
					});
				}
			};

			void submitForm();
		},
		[
			router,
			formID,
			redirect,
			confirmationType,
			isRecruiteeForm,
			isSubmitting,
			formatFormData,
			openQuestions,
			isFlexmailForm,
			flexmailSourceID,
			locale,
			slug,
			t,
		],
	);

	return (
		<Container size="sm" className="form__container">
			{enableIntro && introContent && !hasSubmitted && (
				<RichText
					className="mb-8 lg:mb-12 rich-text-intro"
					data={introContent}
					enableGutter={false}
				/>
			)}
			<div>
				<FormProvider {...formMethods}>
					{!isLoading && hasSubmitted && confirmationType === "message" && (
						<RichText
							enableGutter={false}
							className="rich-text"
							data={confirmationMessage}
						/>
					)}
					{isLoading && !hasSubmitted && (
						<p className="text-loading">Loading, please wait...</p>
					)}
					{error && (
						<div className="rich-text">
							<p>{`${error.message || ""}`}</p>
						</div>
					)}
					{!hasSubmitted && (
						<form
							id={formID}
							className="form__form"
							onSubmit={handleSubmit(onSubmit)}
						>
							<div className="form--fields">
								{formFromProps?.fields?.map((field, index) => {
									// eslint-disable-next-line @typescript-eslint/no-explicit-any
									const Field: React.FC<any> =
										fields?.[field.blockType as keyof typeof fields];
									if (Field) {
										return (
											<div
												// biome-ignore lint/suspicious/noArrayIndexKey: <explanation>
												key={index}
												className={`form__field ${field.blockType}`}
											>
												<Field
													form={formFromProps}
													{...field}
													{...formMethods}
													control={control}
													errors={errors}
													register={register}
												/>
											</div>
										);
									}
									return null;
								})}
								{openQuestions &&
									openQuestions.length > 0 &&
									[...openQuestions]
										.sort((a, b) => (a.position ?? 0) - (b.position ?? 0))
										.map((question) => (
											<div
												key={question.questionId}
												className={`form__field ${question.kind}`}
											>
												<OpenQuestionField
													question={question}
													register={register}
													control={control}
													errors={errors}
													setValue={setValue}
												/>
											</div>
										))}
								{!disableCaptcha &&
									process.env.NEXT_PUBLIC_TURNSTILE_SITE_KEY && (
										<Turnstile
											siteKey={process.env.NEXT_PUBLIC_TURNSTILE_SITE_KEY}
											onSuccess={(token) => {
												turnstileToken.current = token;
											}}
										/>
									)}
								<Button
									form={formID}
									type="submit"
									variant="secondary"
									disabled={isSubmitting}
								>
									{submitButtonLabel}
								</Button>
							</div>
						</form>
					)}
				</FormProvider>
			</div>
		</Container>
	);
};
